import { useState, useCallback, useEffect } from 'react';
import { LoadingOverlay } from '@mantine/core';
import { type FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { useParams } from 'react-router-dom';

import useEnsisMutation from '../../hooks/useEnsisMutation';
import { handleFileUploadError } from '../../utils/apiUtils';
import { withAnalyticsEvent } from '../../utils/analyticsUtils';
import ModalContent from '../ModalContent';
import Dropzone from '../Dropzone';

interface Props {
  onClose: () => void
  setSelected?: (uids: string[]) => void
  onUploadImages?: () => void
}

const ACCEPTED_IMAGE_FILE_TYPES = [
  MIME_TYPES.jpeg,
  MIME_TYPES.png
];

const ACCEPTED_IMAGE_FILE_STRING = 'JPEG or PNG';
export interface ImageFileWithMetadata {
  fileObject: FileWithPath
}
const UploadImages: React.FC<Props> = (props: Props) => {
  const { onClose, setSelected, onUploadImages } = props;
  const { organization } = useParams();
  const [files, setFiles] = useState<ImageFileWithMetadata[]>([]);
  const successMessage = files.length > 1 ? 'Images added' : 'Image added';

  const uploadImagesMutation = useEnsisMutation(
    '/app/organization-images',
    {
      contentType: 'multipart/form-data',
      requestType: 'post',
      showFailureMessage: false,
      successMessage,
      onSuccess: () => {
        if (onUploadImages !== undefined) {
          onUploadImages();
        }
        onClose();
      },
      onError: handleFileUploadError,
      queryKeysToInvalidate: ['/app/organization-images']
    }
  );

  const onSubmit = useCallback(() => {
    const filesToUpload = files.map((file) => file.fileObject) as unknown as string[];
    uploadImagesMutation.mutate({
      files: filesToUpload,
      organization_slug: organization ?? ''
    });
  }, [files]);

  useEffect(() => {
    if (uploadImagesMutation.data !== undefined && setSelected !== undefined) {
      const imageUids = uploadImagesMutation.data.map((item) => item.uid);
      setSelected(imageUids);
    }
  }, [uploadImagesMutation.data]);

  return (
    <ModalContent
      title='Import images'
      subtitle='Import images or graphics'
      onClose={onClose}
      primaryButton={{
        label: 'Import',
        onClick: withAnalyticsEvent(onSubmit, 'AddImage'),
        disabled: files.length === 0
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: onClose
      }}
    >
      <LoadingOverlay visible={uploadImagesMutation.isPending} zIndex={1000} />
      <Dropzone
        files={files}
        acceptedFileTypes={ACCEPTED_IMAGE_FILE_TYPES}
        acceptedFilesString={ACCEPTED_IMAGE_FILE_STRING}
        setFiles={setFiles}
        showResourceTypeDropdown={false}
        instructionText='Click or drag and drop to upload new images or graphics.'
      />
    </ModalContent>);
};

export default UploadImages;
